import { css } from 'aphrodite';
import * as React from 'react';
import { Control, Controller, FieldErrors } from 'react-hook-form';
import { Input } from '../../../../../../../../web/components-v2/Input';
import { Pill } from '../../../../../../../../web/components-v2/Pill/Pill';
import { GuestEmailMode, IEventTicket, IPublicFileAttachment, IRecipient } from '../../../../../../../models/Api';
import { styleSheet } from '../styles';

interface ITicketHolder {
	id?: string; // Unique ID from backend or generated UUID
	ticketId: string;
	ticketName: string;
	firstName: string;
	lastName: string;
	email: string;
	phone: string;
	isPaid?: boolean; // Whether this ticket has been paid for
	ticketItemIndex?: number | null; // Which physical ticket (table) this seat belongs to; undefined for single-seat tickets
}

interface IFormValues {
	isAttending: boolean | null;
	isPaid: boolean;
	bringingGuest?: boolean;
	email?: string;
	firstName?: string;
	lastName?: string;
	phone?: string;
	guests: IRecipient[];
	customProperties: Record<string, string | IPublicFileAttachment[]>;
	ticketSelections?: Record<string, number>;
	ticketHolders?: ITicketHolder[];
}

interface TicketHolderFieldsProps {
	tickets: IEventTicket[];
	ticketSelections: Record<string, number>;
	ticketHolders: ITicketHolder[]; // Actual ticket holder data from form
	control: Control<IFormValues>;
	errors: FieldErrors<IFormValues>;
	disabled?: boolean;
	primaryTicketHolderId: string | null;
	onPrimaryTicketHolderChange: (id: string) => void;
	requireGuestNames?: boolean;
	requirePhoneNumber?: boolean;
	guestEmailMode?: GuestEmailMode;
}

export const TicketHolderFields: React.FC<TicketHolderFieldsProps> = ({
	tickets: _tickets,
	ticketSelections: _ticketSelections,
	ticketHolders,
	control,
	errors,
	disabled = false,
	primaryTicketHolderId,
	onPrimaryTicketHolderChange,
	requireGuestNames = false,
	requirePhoneNumber = false,
	guestEmailMode = GuestEmailMode.Optional,
}) => {
	// Calculate total paid tickets
	const totalPaidTickets = React.useMemo(() => {
		return ticketHolders.filter(h => h.isPaid).length;
	}, [ticketHolders]);

	// Check if there's a paid primary ticket holder
	const hasPaidPrimary = React.useMemo(() => {
		const primaryHolder = ticketHolders.find(h => h.id === primaryTicketHolderId);
		return primaryHolder?.isPaid || false;
	}, [ticketHolders, primaryTicketHolderId]);

	// Don't render if no ticket holders
	if (ticketHolders.length === 0) {
		return null;
	}

	return (
		<div className={css(styleSheet.ticketHolderContainer)}>
			<div className={css(styleSheet.ticketHolderHeadingRow)}>
				<h3 className={css(styleSheet.ticketHolderHeading)}>Ticket Holder Information</h3>
				{totalPaidTickets > 0 && <Pill label={`Paid Tickets: ${totalPaidTickets}`} variant='label' color={'green'} />}
			</div>

			<div className={css(styleSheet.ticketHolderCards)}>
				{ticketHolders.map((holder, index) => {
					const isPrimary = holder.id === primaryTicketHolderId;
					const isPaid = holder.isPaid || false;
					const ticketCount = ticketHolders.filter(h => h.ticketId === holder.ticketId).length;
					const ticketNumber = ticketHolders.filter((h, i) => h.ticketId === holder.ticketId && i <= index).length;
					// Multi-seat tickets group seats into tables via ticketItemIndex.
					const isMultiSeat = holder.ticketItemIndex != null;
					const seatNumberInTable = ticketHolders.filter(
						(h, i) => h.ticketId === holder.ticketId && h.ticketItemIndex === holder.ticketItemIndex && i <= index
					).length;
					// Render a table heading before the first seat of each table so each group's
					// name fields are visibly associated with their own table.
					const previousHolder = index > 0 ? ticketHolders[index - 1] : undefined;
					const isTableStart =
						isMultiSeat &&
						(!previousHolder ||
							previousHolder.ticketId !== holder.ticketId ||
							previousHolder.ticketItemIndex !== holder.ticketItemIndex);
					const seatsPerTable = _tickets.find(t => t.id === holder.ticketId)?.attendeeCountPerItem;
					const tableHeading = `${holder.ticketName} — Table ${holder.ticketItemIndex}${
						seatsPerTable ? ` (up to ${seatsPerTable} seats)` : ''
					}`;
					const cardTitle = isMultiSeat
						? `Seat ${seatNumberInTable}${isPrimary ? ' (you)' : ''}`
						: `${holder.ticketName} ${ticketCount > 1 ? `#${ticketNumber}` : ''}`;
					// Guest ticket holders (non-primary) are only required if requireGuestNames is true
					const isRequired = isPrimary || requireGuestNames;
					const requiredLabel = isRequired ? <span className={css(styleSheet.required)}>*</span> : null;

					return (
						<React.Fragment key={`holder-${holder.id}`}>
							{isTableStart && <h4 className={css(styleSheet.tableGroupHeading)}>{tableHeading}</h4>}
							<div className={css(styleSheet.ticketHolderCard)}>
								<div className={css(styleSheet.ticketHolderCardHeader)}>
									<div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
										<h4 className={css(styleSheet.ticketHolderCardTitle)}>{cardTitle}</h4>
										{isPaid && <Pill label='PAID' variant='label' color='green' />}
									</div>
									{/* Show primary selection only when ticket is not paid and no paid primary exists */}
									{!isPaid && !hasPaidPrimary ? (
										<label className={css(styleSheet.primaryTicketLabel)}>
											<input
												type='radio'
												name='primaryTicketHolder'
												checked={isPrimary}
												onChange={() => holder.id && onPrimaryTicketHolderChange(holder.id)}
												disabled={disabled}
												className={css(styleSheet.primaryTicketRadio)}
											/>
											<span className={css(styleSheet.primaryTicketText)}>
												{isPrimary ? 'Primary Ticket Holder' : 'Make Primary Ticket Holder'}
											</span>
										</label>
									) : (
										/* When ticket is paid, only show "Primary Ticket Holder" label for primary */
										isPrimary && <span className={css(styleSheet.primaryTicketText)}>Primary Ticket Holder</span>
									)}
								</div>

								<div className={css(styleSheet.ticketHolderFields)}>
									{/* First Name */}
									<div>
										<label
											htmlFor={`ticketHolders.${index}.firstName`}
											className={css(styleSheet.ticketHolderFieldLabel)}
										>
											First Name {requiredLabel}
										</label>
										<Controller
											name={`ticketHolders.${index}.firstName`}
											control={control}
											render={({ field }) => (
												<Input
													id={`ticketHolders.${index}.firstName`}
													type='text'
													value={field.value || ''}
													onChange={(e: React.ChangeEvent<HTMLInputElement>) => field.onChange(e.target.value)}
													disabled={disabled || isPrimary}
													styles={disabled || isPrimary ? [styleSheet.inputDisabled] : undefined}
												/>
											)}
										/>
										{errors.ticketHolders?.[index]?.firstName && (
											<p className={css(styleSheet.errorMessage, styleSheet.noMargin)}>
												{errors.ticketHolders[index]?.firstName?.message}
											</p>
										)}
									</div>

									{/* Last Name */}
									<div>
										<label
											htmlFor={`ticketHolders.${index}.lastName`}
											className={css(styleSheet.ticketHolderFieldLabel)}
										>
											Last Name {requiredLabel}
										</label>
										<Controller
											name={`ticketHolders.${index}.lastName`}
											control={control}
											render={({ field }) => (
												<Input
													id={`ticketHolders.${index}.lastName`}
													type='text'
													value={field.value || ''}
													onChange={(e: React.ChangeEvent<HTMLInputElement>) => field.onChange(e.target.value)}
													disabled={disabled || isPrimary}
													styles={disabled || isPrimary ? [styleSheet.inputDisabled] : undefined}
												/>
											)}
										/>
										{errors.ticketHolders?.[index]?.lastName && (
											<p className={css(styleSheet.errorMessage, styleSheet.noMargin)}>
												{errors.ticketHolders[index]?.lastName?.message}
											</p>
										)}
									</div>

									{/* Email */}
									{isPrimary || guestEmailMode !== GuestEmailMode.Off ? (
										<div>
											<label
												htmlFor={`ticketHolders.${index}.email`}
												className={css(styleSheet.ticketHolderFieldLabel)}
											>
												Email{' '}
												{isPrimary || guestEmailMode === GuestEmailMode.Required ? (
													<span className={css(styleSheet.required)}>*</span>
												) : null}
											</label>
											<Controller
												name={`ticketHolders.${index}.email`}
												control={control}
												render={({ field }) => (
													<Input
														id={`ticketHolders.${index}.email`}
														type='email'
														value={field.value || ''}
														onChange={(e: React.ChangeEvent<HTMLInputElement>) => field.onChange(e.target.value)}
														disabled={disabled || isPrimary}
														styles={disabled || isPrimary ? [styleSheet.inputDisabled] : undefined}
													/>
												)}
											/>
											{errors.ticketHolders?.[index]?.email && (
												<p className={css(styleSheet.errorMessage, styleSheet.noMargin)}>
													{errors.ticketHolders[index]?.email?.message}
												</p>
											)}
										</div>
									) : null}

									{/* Phone */}
									<div>
										<label htmlFor={`ticketHolders.${index}.phone`} className={css(styleSheet.ticketHolderFieldLabel)}>
											Phone {isPrimary && requirePhoneNumber ? requiredLabel : null}
										</label>
										<Controller
											name={`ticketHolders.${index}.phone`}
											control={control}
											render={({ field }) => (
												<Input
													id={`ticketHolders.${index}.phone`}
													type='tel'
													value={field.value || ''}
													onChange={(e: React.ChangeEvent<HTMLInputElement>) => field.onChange(e.target.value)}
													disabled={disabled || isPrimary}
													styles={disabled || isPrimary ? [styleSheet.inputDisabled] : undefined}
												/>
											)}
										/>
										{errors.ticketHolders?.[index]?.phone && (
											<p className={css(styleSheet.errorMessage, styleSheet.noMargin)}>
												{errors.ticketHolders[index]?.phone?.message}
											</p>
										)}
									</div>
								</div>

								{/* Hidden fields for ticket metadata */}
								<Controller
									name={`ticketHolders.${index}.ticketId`}
									control={control}
									defaultValue={holder.ticketId}
									render={({ field }) => <input type='hidden' {...field} />}
								/>
								<Controller
									name={`ticketHolders.${index}.ticketName`}
									control={control}
									defaultValue={holder.ticketName}
									render={({ field }) => <input type='hidden' {...field} />}
								/>
								<Controller
									name={`ticketHolders.${index}.ticketItemIndex`}
									control={control}
									defaultValue={holder.ticketItemIndex}
									render={({ field }) => <input type='hidden' {...field} value={field.value ?? ''} />}
								/>
							</div>
						</React.Fragment>
					);
				})}
			</div>
		</div>
	);
};
